import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useIsAuthenticated } from '@azure/msal-react';
import { useHistory } from 'react-router-dom';

import {
  YOUR_INFO_STEPS,
  TEN_DIGIT_NUMBER,
  STICKY_BAR_HEIGHT_SECOND_STEP,
  DEFAULT_STICKY_BAR_HEIGHT,
  AFTER_RATE_TAG,
} from '../../../constants';
import {
  createDiamondClient,
  loadAmericanStates,
  petTagDateOfBirthChanged,
  sendAllPetsFSCLead,
  sendFSCCustomerLead,
  sendFSCQuoteLead,
  validateDiamondUser,
  validateIfEmailExists,
  yourInfoStepChanged,
} from '../../../actions';
import {
  useCallbackAfterRate,
  useKameleoonVisitedPageEvent,
  useQuoteTotal,
  useRateSummary,
  useSavedElement,
} from '../../../hooks';
import { isValidDiamondStreet, validatePowerupsSelected } from '../../../util';

import ill14 from '../../../assets/ills/ill-14.svg';
import ill9 from '../../../assets/ills/ill-9.svg';
import ill4 from '../../../assets/ills/ill-4.svg';

import { PetParentInfo } from './PetParentInfo';
import PlanSummary from './PaymentSummary';
import { PaymentMethod } from './PaymentMethod';
import { YourInfoFooter } from './YourInfoFooter';
import TermsAndConditions from './TermsAndConditions';
import PaymentBreakdown from './PaymentBreakdown';
import DiscountApplied from './DiscountApplied';
import ModalCustomerWelcome from '../../common/ModalCustomerWelcome';
import { YourFirstPayment } from './YourFirstPayment';
import { FigoLoadingOverlay } from '../../common/FigoLoadingOverlay';
import ImportantDatesNewDesign from './ImportantDatesNewDesign';
import CardPlanMainHeaderCompany from '../CardPlanMainHeaderCompany';
import { Prop103DisclaimerText } from './Prop103DisclaimerText';

import './YourInfoContent.css';
import { TemporaryMessage } from '../TemporaryMessage';
import { QuestionsSection } from '../QuestionsSection';
import { QuoteSizeWarning } from '../QuoteSizeWarning';
import { IncompleteSelectionError } from '../IncompleteSelectionError';

const YourInfoContent = ({ priceToCompare }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { goToSavedElementAndForgot } = useSavedElement();
  useKameleoonVisitedPageEvent();
  const store = useSelector(({ quoting }) => quoting, shallowEqual);
  const {
    americanStates,
    data,
    isLoading,
    newCustomer,
    newCustomer: { address, email, name, lastName, phoneNumber },
    petTags,
    sessionInformation,
    stripe: { stripeError, paymentMethod },
    termsAndConditions,
    petQuoteSelected,
    searchParameters,
    yourInfoStep,
  } = store;
  const { isCustomer, nopCommerceUser } = sessionInformation;
  const [
    validateTermsAndConditions,
    setValidateTermsAndConditions,
  ] = useState(0);
  const [validatePetParentInfo, setValidatePetParentInfo] = useState(0);
  const [validatePetCloudLogin, setValidatePetCloudLogin] = useState(0);
  const isSecondStep = yourInfoStep === YOUR_INFO_STEPS.secondStep;
  const [yourVeterinarianRef, setYourVeterinarianRef] = useState(null);
  const [scrollbarDown, setScrollbarDown] = useState(false);
  const isAuthenticated = useIsAuthenticated();

  const [scrollTermCondition, setScrollTermCondition] = useState(false);
  const termConditionsRef = useRef();

  const [scrollPaymentMethod, setScrollPaymentMethod] = useState(false);
  const paymentMethodRef = useRef();
  const [showPaymentMethodError, setShowPaymentMethodError] = useState(false);

  const [scrollPetParentInfo, setScrollPetParentInfo] = useState(false);
  const [goToFirstErrorInput, setGoToFirstErrorInput] = useState(false);
  const petParentInfoRef = useRef();
  const quoteTotal = useQuoteTotal();

  const tokenId = paymentMethod?.token;

  const [
    scrollPetParentInfoSecond,
    setScrollPetParentInfoSecond,
  ] = useState(false);
  const petParentInfoSecondRef = useRef();

  const stripeRef = useRef();

  const useProp103 = useMemo(() => {
    if (store.isLoading || store.petRemovedLoading) {
      return false;
    }

    if (!store.data) {
      return false;
    }

    return store.data.SiteMessages
      .find((item) => item.Name === 'PROP103') || false;
  }, [store.data, store.isLoading, store.petRemovedLoading]);

  const { getSelectedRate } = useRateSummary();

  useEffect(() => {
    if (isSecondStep) {
      goToSavedElementAndForgot();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSecondStep]);

  useEffect(() => {
    async function validateCustomerB2C() {
      if (newCustomer.email
        && !sessionInformation.nopCommerceUser.LoginSuccess
        && !isAuthenticated) {
        const payload = await validateIfEmailExists(newCustomer.email);
        const { userAlreadyExists, ...restData } = payload;

        dispatch(validateDiamondUser({
          customerExistsData: restData,
          email: newCustomer.email,
          userAlreadyExists,
          validateCustomer: false,
        }));
      }
    }

    validateCustomerB2C();

    getSelectedRate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redirectToStep1 = useCallback(() => {
    const url = `/quoting/getQuote${searchParameters}`;
    history.push(url);
  }, [history, searchParameters]);

  useCallbackAfterRate(() => {
    getSelectedRate();
  }, [
    AFTER_RATE_TAG.newZipCode,
    AFTER_RATE_TAG.removePet,
    AFTER_RATE_TAG.updatePet,
  ]);

  useCallbackAfterRate(() => {
    const petQuoteRateItem = data.ebPetQuoteResponseList
      .find((pqRateItem) => pqRateItem.petQuoteId === petQuoteSelected);
    const powerUpValidation = validatePowerupsSelected(petQuoteRateItem);
    dispatch(sendFSCQuoteLead());
    if (!powerUpValidation.isValid || priceToCompare !== quoteTotal) {
      window.scrollTo(0, 0);
      redirectToStep1();
    }
  }, [AFTER_RATE_TAG.updatePet]);

  // TODO - implement validation for personalize date
  const isPersonalizeValid = true;

  const isTermsAndConditionsValid =
    termsAndConditions.agreeTermsAndConditionCheck
    && termsAndConditions.agreePaymentAndRenewalCheck;
  const isPetParentInfoValid = newCustomer.name.trim().length
    && newCustomer.lastName.trim().length
    && newCustomer.phoneNumber.trim().length
    && TEN_DIGIT_NUMBER.test(newCustomer.phoneNumber.trim())
    && newCustomer.employeeID.trim().length
    && newCustomer.address.city.trim().length
    && newCustomer.address.street.trim().length
    && isValidDiamondStreet(newCustomer.address.street.trim());
  const isSecondParentInfoValid =
    !store.secondParentFormVisible
    || (store.secondParentFormVisible
      && newCustomer.secondParent.name.trim().length
      && newCustomer.secondParent.lastName.trim().length
      && newCustomer.secondParent.phoneNumber.trim().length
      && TEN_DIGIT_NUMBER.test(newCustomer.secondParent.phoneNumber.trim()));

  const isNewCustomerValid = newCustomer.email.trim().length
    && !newCustomer.emailError.trim().length;

  const petCloudLoginSuccess = isNewCustomerValid || isCustomer;

  const isPurchaseValid = isTermsAndConditionsValid
    && isPersonalizeValid
    && isPetParentInfoValid
    && isSecondParentInfoValid
    && petCloudLoginSuccess
    && tokenId?.length > 0;

  useEffect(() => {
    if (isSecondStep && yourVeterinarianRef) {
      window.scrollTo({
        behavior: 'smooth',
        top: yourVeterinarianRef.offsetTop - 100,
      });
    } else if (!yourVeterinarianRef) {
      const element = document.getElementById('yourVeterinarianRef');
      setYourVeterinarianRef(element);
    }
  }, [yourVeterinarianRef, setYourVeterinarianRef, isSecondStep]);

  useEffect(() => {
    if (scrollTermCondition) {
      window.scrollTo({
        behavior: 'smooth',
        top: termConditionsRef.current.offsetTop - 80,
      });
      termConditionsRef.current.focus();
      setScrollTermCondition(false);
    }
  }, [scrollTermCondition]);

  useEffect(() => {
    if (scrollPaymentMethod) {
      window.scrollTo({
        behavior: 'smooth',
        top: paymentMethodRef.current.offsetTop,
      });

      setScrollPaymentMethod(false);
    }
  }, [scrollPaymentMethod]);

  useEffect(() => {
    if (scrollPetParentInfo) {
      window.scrollTo({
        behavior: 'smooth',
        top: petParentInfoRef.current.offsetTop,
      });
      setScrollPetParentInfo(false);
      setGoToFirstErrorInput(true);
    }
  }, [scrollPetParentInfo]);

  useEffect(() => {
    if (goToFirstErrorInput) {
      setGoToFirstErrorInput(false);
    }
  }, [goToFirstErrorInput]);

  useEffect(() => {
    if (scrollPetParentInfoSecond) {
      window.scrollTo({
        behavior: 'smooth',
        top: petParentInfoSecondRef.current.offsetTop,
      });
      setScrollPetParentInfoSecond(false);
      setGoToFirstErrorInput(true);
    }
  }, [scrollPetParentInfoSecond]);

  useEffect(() => {
    if (stripeError === null) {
      return;
    }

    setScrollPaymentMethod(stripeError);
  }, [stripeError]);

  const sendCustomerLead = useCallback(() => {
    if (newCustomer.email !== newCustomer.initialEmail) {
      dispatch(sendAllPetsFSCLead());
      dispatch(sendFSCQuoteLead());
    }
    dispatch(sendFSCCustomerLead());
  }, [dispatch, newCustomer]);

  useEffect(() => {
    if (americanStates.length <= 0) {
      dispatch(loadAmericanStates());
    }
  }, [americanStates, dispatch]);

  function createCustomer() {
    if (isCustomer && nopCommerceUser.DiamonClientdId) {
      return;
    }

    dispatch(createDiamondClient({
      address: address.street,
      city: address.city,
      clientId: nopCommerceUser.DiamonClientdId,
      email,
      firstName: name,
      lastName,
      phoneNumber,
      stateAbbreviation: address.stateAbbr,
      stateName: address.state,
      zipCode: address.zipCode,
    }));
  }

  useEffect(() => {
    if (tokenId) {
      setShowPaymentMethodError(false);
    }
  }, [tokenId]);

  const handleContinuePurchaseClick = async () => {
    if (isSecondStep) {
      setValidateTermsAndConditions(validateTermsAndConditions + 1);

      setScrollPaymentMethod(false);
      setShowPaymentMethodError(false);
    }
    setValidatePetParentInfo(validatePetParentInfo + 1);
    setValidatePetCloudLogin(validatePetCloudLogin + 1);

    if (!isPetParentInfoValid) {
      setScrollPetParentInfo(true);
    } else if (!isSecondParentInfoValid) {
      setScrollPetParentInfoSecond(true);
    } else if ((!tokenId) && isSecondStep) {
      setScrollPaymentMethod(true);
      setShowPaymentMethodError(true);
      // trigger stripe elements validations
      stripeRef.current?.validateStripe?.();
    } else if (!isTermsAndConditionsValid && isSecondStep) {
      setScrollTermCondition(true);
    }

    if (isPetParentInfoValid && isSecondParentInfoValid
      && petCloudLoginSuccess) {
      createCustomer();
      if (!isSecondStep) {
        sendCustomerLead();
      }
      dispatch(yourInfoStepChanged(YOUR_INFO_STEPS.secondStep));
    }
  };

  const showLoadingOverlay = useMemo(() => isLoading
    || sessionInformation.isLoading
    || store.isLoadingNewAddress
    || store.purchase.loading
    || store.rateSummary.loading, [
    isLoading,
    sessionInformation.isLoading,
    store.isLoadingNewAddress,
    store.purchase.loading,
    store.rateSummary.loading]);

  const birthPurchaseDate = new Date();
  birthPurchaseDate.setFullYear(birthPurchaseDate.getFullYear() - 1);

  const formattedDateMinusOneYear =
    birthPurchaseDate.toLocaleDateString('en-US').replace(/\//g, '-');

  const handleScroll = () => {
    if (document.body) {
      setScrollbarDown(
        document.body.getBoundingClientRect().top <= -10,
      );
    }
  };

  useEffect(() => {
    petTags.forEach((petTag) => {
      const { petQuoteId } = petTag;
      dispatch(petTagDateOfBirthChanged({
        petDateOfBirth: formattedDateMinusOneYear,
        petQuoteId,
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, formattedDateMinusOneYear]);

  useEffect(() => {
    // Free user don't have DiamondClientId only CustomerId
    const isFreeUser = sessionInformation.isCustomer
      && !nopCommerceUser.DiamonClientdId
      && nopCommerceUser.CustomerNopCommerceId;
    const isLoadingUser = sessionInformation.isLoading
      || sessionInformation.loadingLogin;

    if (!isSecondStep || !isFreeUser || isLoadingUser) {
      return;
    }

    // create diamondId guess
    createCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSecondStep, sessionInformation]);

  useEffect(() => {
    // Free user don't have DiamondClientId only CustomerId
    const isFreeUser = sessionInformation.isCustomer
      && !nopCommerceUser.DiamonClientdId
      && nopCommerceUser.CustomerNopCommerceId;
    const isLoadingUser = sessionInformation.isLoading
      || sessionInformation.loadingLogin;

    if (!isSecondStep || !isFreeUser || isLoadingUser) {
      return;
    }

    // create diamondId guess
    createCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSecondStep, sessionInformation]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', () => handleScroll);
    };
  }, []);

  return (
    <>
      <FigoLoadingOverlay
        visible={showLoadingOverlay}
      />

      <ModalCustomerWelcome isSecondStep />

      <div className={'Your-info-ills-container'
        + `${isSecondStep ? ' Your-info-ills-justify' : ''}`}
      >
        <div className="Your-info-ill-container">
          <img alt="" aria-hidden src={ill14} />
        </div>

        {isSecondStep && (
          <>
            <div className="Your-info-ill-container">
              <img alt="" aria-hidden src={ill9} />
            </div>

            <div className="Your-info-ill-container">
              <img alt="" aria-hidden src={ill4} />
            </div>
          </>
        )}
      </div>

      <div
        className={'Your-info-main-container'
          + `${scrollbarDown ? ' Your-info-scrolled' : ''}`}
      >

        <div className="Your-info-container-company-header">
          <CardPlanMainHeaderCompany />
        </div>

        <div className="Your-info-step-container">
          <QuoteSizeWarning />

          <IncompleteSelectionError />

          <PetParentInfo
            containerRef={petParentInfoRef}
            containerSecondaryRef={petParentInfoSecondRef}
            focusInputWithError={goToFirstErrorInput}
            sendCustomerLead={sendCustomerLead}
            validatePetParentInfo={validatePetParentInfo}
          />
        </div>

        {isSecondStep && (
          <div className="Your-info-step-container Your-info-second-step">
            <PlanSummary />

            <DiscountApplied isSecondStep={isSecondStep} />

            <PaymentBreakdown />

            {useProp103 && <Prop103DisclaimerText />}

            <YourFirstPayment />

            <PaymentMethod
              containerRef={paymentMethodRef}
              email={email}
              name={`${name} ${lastName}`}
              phoneNumber={phoneNumber}
              showError={showPaymentMethodError}
              stripeRef={stripeRef}
            />

            <ImportantDatesNewDesign />

            <TermsAndConditions
              containerRef={termConditionsRef}
              triggerValidation={validateTermsAndConditions}
            />
          </div>
        )}

        {!isSecondStep && (
          <div className="Your-info-step-container Your-info-footer">
            <YourInfoFooter
              handleContinuePurchaseClick={handleContinuePurchaseClick}
              isPurchaseValid={isPurchaseValid}
              isSecondStep={isSecondStep}
              petCloudLoginSuccess={petCloudLoginSuccess}
            />
          </div>
        )}
      </div>

      <TemporaryMessage
        positionBottom={isSecondStep
          ? STICKY_BAR_HEIGHT_SECOND_STEP
          : DEFAULT_STICKY_BAR_HEIGHT}
        sticky
      />

      {isSecondStep && (
        <YourInfoFooter
          handleContinuePurchaseClick={handleContinuePurchaseClick}
          isPurchaseValid={isPurchaseValid}
          isSecondStep={isSecondStep}
          petCloudLoginSuccess={petCloudLoginSuccess}
        />
      )}

      <QuestionsSection isQuote />
    </>
  );
};

export { YourInfoContent };
